import InstanceFactory from "../../src/factories/InstanceFactory";
import GameModel from "../../src/model/GameModel";
import SoundVars from "../const/SoundVars";
import { DefaultLayers } from "../utils/Layers";
import SoundController from "../utils/SoundController";
import ViewManager from "../utils/ViewManager";
import BaseView from "./BaseView";
import GameEvents from "../../src/const/GameEvents";
import WinlinesView from "./WinlinesView";
import WinSymbolsView from "./WinSymbolsView";
import BaseAnticipation from "./BaseAnticipation";
import Reel, { ReelOrientation } from "./Reel";
import ReelSymbol from "./Symbol";
import { Timeline } from "../Timeline";
import { Tween } from "./../Tween";
import App from "../App";
import TextField from "./components/TextField";
import betModel from "../../src/model/BetModel";
import SpinModel from "../../src/model/SpinModel";
import {
    IWinData,
    ISpinificationData,
    OverlayType,
} from "../../src/server/data/ISpinData";
import Layout from "../utils/Layout";
import SceneView from "./SceneView";
import AbstractReelBehaviour from "../spinners/AbstractReelBehaviour";
import SymbolAnimation from "./SymbolAnimation";
import Utils from "../utils/Utils";
import ConsoleUtil from "../utils/ConsoleUtil";
import PointerEvents from "../const/PointerEvents";
import CommandManager from "../commands/CommandManager";
import { Commands } from "../commands/CommandEnum";
import PromiseHelpers from "../utils/PromiseHelpers";
import ColorfulTrail from "./ColorfulTrail";
import stateMachine from "./../states/StateMachine";
import ShowWinState from "../states/ShowWinState";
import Trail from "./Trail";
import { TrailPool } from "./TrailPool";
import { Color } from "pixi-particles";
import { WayAnalyzer } from "./WayAnalyzer";
import Layer from "../utils/Layer";
import {
    Point,
    Container,
    Graphics,
    DisplayObject,
    InteractionEvent,
    utils as PixiUtils,
    Sprite,
} from "pixi.js";
import CurrencyUtils from "../utils/CurrencyUtils";
import BetModel from "../../src/model/BetModel";
import { IObjectConfig } from "../baseConfigs/layoutConfigShema";
import BuyFeatureModel from "../model/BuyFeatureModel";
import ConfigManager from "../managers/ConfigManager";
import { ConfigurationTypes } from "../const/ConfigurationTypes";
import Configs from "../../../src/vendorConfigs/Configs";

/**
 * A view which is a group of all the reels
 */
class ReelGroup extends BaseView {
    public static readonly EMPTY_SYM: number = -1;

    // public bg: Graphics;
    public reels: Reel[];
    public spinners: AbstractReelBehaviour[];
    public allResultsShown: boolean;
    public winlinesView: WinlinesView;
    public winSymbolsView: WinSymbolsView;
    public anticipation: BaseAnticipation;
    public hasAnticipation: boolean;
    public anticipationStarted: boolean;
    public winSound: Howl;
    protected winFrameContainer: Container;
    protected symContainer: Container;
    protected winInfoLabelBg: Sprite;
    protected winInfoLabelTF: TextField;
    protected spinDelayedCalls: Tween[];
    protected reelsSpinning: number;
    protected reelStopping: number;
    protected isMinSpinTimeComplete: boolean;
    protected stopSymbolsAvailable: boolean;
    protected stoppingStarted: boolean;
    protected stopDelayedCalls: Tween[];
    protected spinificationData?: ISpinificationData;
    // protected stopImmediatelyFlag: boolean;
    protected winLineTimeline: Timeline;
    protected symAnimCount: number = 0;
    private winGroupToAnim: number = 0;
    protected stopDelay: number = 0;
    private swipePos: number = 0;
    protected resultData: any[][];
    protected groupConfig: {
        winLineLayer?: string;
        celеbrationLayer?: string;
        winSymbolsLayer?: string;
        reelExtraSymbols?: number;
    };
    protected symBounds: IObjectConfig;
    protected spinTime: number;
    protected scene: SceneView;
    protected _onSwipeCancel: any;

    protected symACount: number = 0;

    private animCount: number = 0;
    protected tweenInstances: Tween[];
    protected trailPool: TrailPool;
    protected waysLinesAboveSymbols: boolean;
    // override this if symbols are jumping during turbo spins(or held down space bar)
    // default set should be 1
    protected reelTurboTimeScale: number;
    public skimSymbolsDim: { x: number; y: number }[] = [];
    get emptySym() {
        return ReelGroup.EMPTY_SYM;
    }

    get symbolBounds() {
        return this.symBounds;
    }
    /**
     * Used to visualize the reel group mask as pixi graphics for debug purposes.
     * This is either controlled by debugBounds=true url param and/or by pressing the keyboard B key.
     *
     * @private
     * @type {Graphics}
     * @memberof ReelGroup
     */
    private reelMaskDebugGraphics: Graphics;

    constructor() {
        super();
        this.tweenInstances = [];
        this.trailPool = new TrailPool();
        this.waysLinesAboveSymbols = false;
    }

    /**
     * Gets the ReelGroup instance from the 'Reels' layer's current scene,
     * in case it is already created, otherwise retuns undefined.
     *
     * @readonly
     * @static
     * @type {ReelGroup}
     * @memberof ReelGroup
     */
    public static get instance(): ReelGroup {
        let layer: Layer = ViewManager.getLayer(DefaultLayers.Reels);
        if (layer) {
            let scene: SceneView = layer.getCurrentScene();
            if (scene && scene.subViews.has("ReelGroup")) {
                return scene.getSubView("ReelGroup") as ReelGroup;
            }
        }

        return undefined;
    }

    /**
     *
     *
     * @param {SceneView} scene
     * @param {any[][]} [symbolData]
     * @param {{
     *             winLineLayer?: string;
     *             celеbrationLayer?: string;
     *             winSymbolsLayer?: string;
     *             reelExtraSymbols?: number;
     *         }} [groupConfig]
     * @param {boolean} [forceLandscapeConfig=true]
     * @param {boolean} [automaskReels=false] Automatically creates a mask around all the reels.
     * It supports any shapes, i.e will create a composite mask. Note that this is heavy operation,
     * so use with caution and only when it is really needed!
     * @memberof ReelGroup
     */
    public create(
        scene: SceneView,
        symbolData?: any[][],
        groupConfig?: {
            winLineLayer?: string;
            celеbrationLayer?: string;
            winSymbolsLayer?: string;
            reelExtraSymbols?: number;
        },
        forceLandscapeConfig: boolean = true,
        automaskReels: boolean = false,
        forcedReelOrientation: ReelOrientation = undefined
    ) {
        this.groupConfig = groupConfig || {};
        this.groupConfig.reelExtraSymbols = isNaN(
            this.groupConfig.reelExtraSymbols
        )
            ? this.config.reelExtraSymbols || 0
            : this.groupConfig.reelExtraSymbols;

        if (forceLandscapeConfig) {
            scene.forceLandscape();
        }
        this.parceSceneBounds(scene);
        const positionDefinitions = scene.getPositionDefinitions();
        this.scene = scene;
        const symBounds = positionDefinitions.get("sym");
        this.symBounds = symBounds;
        const reelSeparator = positionDefinitions.get("reel_border");
        const rowSeparator = positionDefinitions.get("row_border");

        this.winlinesView = InstanceFactory.getClass("WinlinesView");
        this.winSymbolsView = InstanceFactory.getClass("WinSymbolsView");
        this.anticipation = InstanceFactory.getClass("Anticipation");
        this.winSymbolsView.on(
            GameEvents.SHOW_SYMBOL_AT,
            this.showSymbolAt.bind(this)
        );
        this.winSymbolsView.on(
            GameEvents.HIDE_SYMBOL_AT,
            this.hideSymbolAt.bind(this)
        );

        this.reels = [];
        this.spinners = [];

        this.symContainer = new Container();
        this.addChild(this.symContainer);
        let sceneBounds =
            scene.sceneBounds || scene.containers.get("reels").getBoundConfig();
        scene.sceneBounds = sceneBounds;
        // const numReels = Math.floor(sceneBounds.width / symBounds.width);
        const numReels = this.calculateReelsCount(scene);
        const numSyms = Math.floor(sceneBounds.height / symBounds.height);
        this.winlinesView.create(
            scene,
            this.groupConfig.winLineLayer,
            this.groupConfig.celеbrationLayer,
            numSyms
        );
        const symPositionsConfig = [];
        for (let reelIdx = 0; reelIdx < numReels; reelIdx++) {
            const reelBounds = positionDefinitions.get("reel_" + reelIdx);
            const reelData = symbolData
                ? symbolData[reelIdx] || SpinModel.getStopSymsForReel(reelIdx)
                : SpinModel.getStopSymsForReel(reelIdx);
            const r = this.gameFactory.getClass("Reel", {
                container: this.symContainer,
                reelIdx,
            }) as Reel;

            let reelSymbolBounds = positionDefinitions.get("sym");
            if (positionDefinitions.has(`sym_${reelIdx}`)) {
                reelSymbolBounds = positionDefinitions.get(`sym_${reelIdx}`);
            }

            r.create(
                reelData,
                {
                    symBounds: reelSymbolBounds,
                    rowSeparator,
                    reelBounds,
                    reelSeparator,
                    extraSymbols: this.groupConfig.reelExtraSymbols,
                    sceneName: scene.name,
                },
                positionDefinitions,
                forcedReelOrientation
            );
            this.reels.push(r);
            const spinner = this.gameFactory.getClass("SpinBehaviour", {
                reel: r,
                sortFunction: this.sortSymbols.bind(this),
            });
            this.spinners.push(spinner);
        }

        this.sortSymbols();
        this.winSymbolsView.create(
            scene,
            this.groupConfig.reelExtraSymbols,
            this.groupConfig.winSymbolsLayer
        );

        scene.onOrientationChange();
        if (automaskReels) {
            this.maskReels();
        }

        let maxSymsInReelCount = 0;
        this.reels.forEach((reel) => {
            maxSymsInReelCount = Math.max(maxSymsInReelCount, reel.numSyms);
        });

        const minimalExtraSymsAllowed = maxSymsInReelCount - 1;
        if (
            this.groupConfig.reelExtraSymbols > 0 &&
            this.groupConfig.reelExtraSymbols < minimalExtraSymsAllowed
        ) {
            throw new Error(
                `'reelExtraSymbols' property at game-config.json is set to ${this.groupConfig.reelExtraSymbols}, but it must be minimum ${minimalExtraSymsAllowed}`
            );
        }
    }

    public getCurrentStoppingReel() {
        return this.reelStopping;
    }

    public setAnticipation(anticipation: BaseAnticipation) {
        this.anticipation = anticipation;
    }

    public setSpinner(spinner: AbstractReelBehaviour, reelId: number) {
        this.spinners[reelId] = spinner;
    }

    public maskReels(mask?: Graphics) {
        const graphics = new Graphics();
        graphics.beginFill(0x000, 0.01);
        this.reels.forEach((r) => {
            r.maskReel(mask);
            graphics.drawRect(r.mask.x, r.mask.y, r.mask.width, r.mask.height);
        });

        graphics.endFill();
        this.mask = graphics;
        this.mask.name = "ReelGroup_Mask";
        this.addChild(this.mask);
        this.parent.mask = null;

        if (App.instance.debugBounds) {
            this.createReelMaskDebugBoundsRect();
        }

        App.dispatcher.on(
            GameEvents.UI_UPDATE,
            () => {
                this.createReelMaskDebugBoundsRect();
            },
            this
        );
    }

    calculateReelsCount(scene): number {
        let count = 0;
        let reel = scene.positions.get("reel_" + count);
        while (reel) {
            count++;
            reel = scene.positions.get("reel_" + count);
        }
        return count;
    }

    parceSceneBounds(scene) {
        if (
            scene.containers &&
            scene.containers.has("reels") &&
            scene.positions
        ) {
            scene.positions.forEach((element) => {
                element.x =
                    element.x -
                    scene.containers.get("reels").getBoundConfig().x;
                element.y =
                    element.y -
                    scene.containers.get("reels").getBoundConfig().y;
            });
        }
    }

    public hideLinesAndInfo(hideSymbols = false) {
        if (hideSymbols) {
            this.stopSymbolAnimations();
        }
        if (this.winSound) {
            SoundController.stopSound(this.winSound);
        }
        this.winlinesView.hideLines();
        this.clearWinInfoLabel();

        // Trail logic code
        Tween.killDelayedCallsTo(this.runTrail);
        Tween.killDelayedCallsTo(this.runMultiTrail);

        this.tweenInstances.forEach((call) => {
            call.kill();
            call = null;
        });

        for (let reelIdx = 0; reelIdx < this.reels.length; reelIdx++) {
            for (
                let symIdx = 0;
                symIdx <
                this.reels[reelIdx].symbols.length -
                2 * GameModel.gameConfig.reelExtraSymbols;
                symIdx++
            ) {
                let symbol = this.getSymbolAt(reelIdx, symIdx);
                let anim = this.winSymbolsView.getAnimAtReelCell(
                    reelIdx,
                    symIdx
                );

                symbol.alpha = 1;
                anim.alpha = 1;
            }
        }

        // this.winSymbolsView.removeChildren();

        this.tweenInstances = [];
        // End of trail logic code
    }

    /**
     * Return reels spinning time in ms
     */
    public getSpinTimePassed(): number {
        let currentTime = BigInt(new Date().getTime());
        let spinPassTime = Number(currentTime - BigInt(this.spinTime));

        return spinPassTime;
    }

    /**
     * Return time needed to all reels to stop (delay for start reels stop animation and stop animation time) in ms
     */
    public getSpinStopTime(): number {
        let result = 0;
        if (
            ConfigManager?.getConfig(ConfigurationTypes.GAME)?.gameType ===
            "normal"
        ) {
            if (GameModel.turboSpin) {
                // Convert in ms
                result = this.config.quickStopTime * 1000;
            } else {
                result =
                    ((this.reels.length - 1) * this.config.reelStopDelayTime +
                        this.config.stopTime) *
                    1000;
            }
        } else {
            if (GameModel.turboSpin) {
                result =
                    Configs.reels.cascadeProps.mainSpinProps
                        .spinSymbolAnimTimeInTurboMode +
                    Configs.reels.cascadeProps.mainSpinProps
                        .stopReelDelayInTurboMode +
                    Configs.reels.cascadeProps.mainSpinProps
                        .stopSymbolDelayInTurboMode;
            } else {
                result =
                    Configs.reels.numberOfReels *
                    Configs.reels.cascadeProps.mainSpinProps.stopReelDelay +
                    Configs.reels.cascadeProps.mainSpinProps
                        .stopSymbolAnimTime +
                    Configs.reels.cascadeProps.mainSpinProps.stopSymbolDelay;
            }
        }

        return result;
    }

    public stopSymbolAnimations() {
        this.off(GameEvents.GROUP_ANIM_COMPLETE);
        for (let i = 0; i < this.reels.length; i++) {
            const reel = this.reels[i];
            const numSyms = reel.symbols.length;
            for (let symIdx = 0; symIdx < numSyms; symIdx++) {
                reel.symbols[symIdx].stopAnimation();
            }
        }
        this.winSymbolsView.stopAll();
    }

    public stopLinesLoop() {
        this.off(GameEvents.GROUP_ANIM_COMPLETE);
    }

    public sortSymbols() {
        if (this.config.symDepthSortOrderById) {
            this.symContainer.children.sort(this.symCompare.bind(this));
            this.winSymbolsView.getChildren().sort(this.symCompare.bind(this));
        }
    }

    public symCompare(s1: DisplayObject, s2: DisplayObject) {
        const id1 =
            s1 instanceof ReelSymbol || s1 instanceof SymbolAnimation
                ? s1.getId()
                : -1;
        const id2 =
            s2 instanceof ReelSymbol || s2 instanceof SymbolAnimation
                ? s2.getId()
                : -1;

        let sortRes = 0;

        const orderIdx1 = this.config.symDepthSortOrderById.indexOf(id1);
        const orderIdx2 = this.config.symDepthSortOrderById.indexOf(id2);

        const z1 =
            s1 instanceof ReelSymbol ||
                (s1 instanceof SymbolAnimation && s1.zOrder)
                ? s1.zOrder
                : 0;
        const z2 =
            s2 instanceof ReelSymbol ||
                (s2 instanceof SymbolAnimation && s2.zOrder)
                ? s2.zOrder
                : 0;

        if (z1 > z2) {
            sortRes = 1;
        } else if (z1 < z2) {
            sortRes = -1;
        } else if (orderIdx1 < orderIdx2) {
            sortRes = 1;
        } else if (orderIdx1 > orderIdx2) {
            sortRes = -1;
        } else {
            sortRes = Utils.sortSymbols(s1, s2, this.config.symPosOrder || {});
        }

        return sortRes;
    }

    public toggleTurbo() {
        for (let reelIdx = 0; reelIdx < this.spinners.length; reelIdx++) {
            const s = this.spinners[reelIdx];
            if (GameModel.turboSpin) {
                s.activateTurbo(this.reelTurboTimeScale);
            } else {
                s.deactivateTurbo();
            }
        }
    }

    public spin(delay: number = 0, easeFunc?: (number) => number) {
        this.spinTime = new Date().getTime();

        if (this.winLineTimeline) {
            this.winLineTimeline.kill();
        }
        this.hasAnticipation = false;
        this.anticipationStarted = false;
        this.spinificationData = null;
        this.reelStopping = 0;
        // const heldReels = spinModel.getPlayerHoldsPattern();
        if (GameModel.turboSpin) {
            delay = 0;
        }
        SoundController.playSound(SoundVars.EFFECT_REEL_START);
        this.spinDelayedCalls = [];
        this.reelsSpinning = 0;
        let reelsToSpin = 0;
        // let delay = 0;
        for (let reelIdx = 0; reelIdx < this.spinners.length; reelIdx++) {
            const s = this.spinners[reelIdx];
            const r = this.reels[reelIdx];
            // r.isHeld = heldReels[reelIdx];
            if (!r.isHeld) {
                let currentDelay = delay * reelsToSpin;
                reelsToSpin++;
                // delay += reelIdx * .075;
                this.spinDelayedCalls.push(
                    this.addDelayedCall(
                        currentDelay,
                        () => {
                            this.reelsSpinning++;
                            s.spin(easeFunc);
                            if (
                                this.reelsSpinning ===
                                this.spinners.length - 1
                            ) {
                                this.emit(GameEvents.ALL_REELS_STARTED);
                            }
                            if (GameModel.turboSpin) {
                                s.activateTurbo(this.reelTurboTimeScale);
                            }
                        },
                        [],
                        s
                    )
                );
            }
        }

        this.isMinSpinTimeComplete = false;
        this.stoppingStarted = false;
        this.stopSymbolsAvailable = false;
        this.stopDelayedCalls = [];
        SpinModel.stopImmediately = false;

        ConsoleUtil.yellow("MIN TIME: " + this.config.minSpinTime);
        ConsoleUtil.yellow("DELAY: " + delay);

        //////////////////////////// This delay maybe don't needed anymore after using min spin time logic in spin start state in Black Hole game ////////////////////////
        let reelDelay;
        if (this.config.individualReelSpinDelayConf) {
            reelDelay =
                (this.config.minSpinTime / 100) *
                this.config.individualReelSpinDelayConf.stopReelsDelay;
        } else {
            reelDelay = Math.max(delay * reelsToSpin, this.config.minSpinTime);
        }

        if (GameModel.turboSpin) {
            reelDelay = Math.max(
                delay * reelsToSpin,
                this.config.quickSpinTime
            );
        }

        this.addDelayedCall(reelDelay, this.allowStop, [], this);

        //////////////////////////// This delay maybe don't needed anymore after using min spin time logic in spin start state in Black Hole game ////////////////////////

        if (this.trailPool) {
            this.trailPool.destroyAll();
        }

        // TODO SOME SORT OF STATE MACHINE
        // if (this.controller.getState() == C_GameState.START_SPIN_1)
        // {
        // this.dispatcher.emit(GameEvents.START_SPIN_1_SEQUENCE_SHOWN);
        // }
        // else
        // {
        //     this.dispatcher.emit(GameEvents.START_SPIN_2_SEQUENCE_SHOWN);
        // }
    }

    public isReelHeld(reelIdx: number): boolean {
        return this.reels[reelIdx].isHeld;
    }

    public holdReel(reelIdx: number) {
        this.reels[reelIdx].isHeld = true;
    }

    public releaseReel(reelIdx: number) {
        this.reels[reelIdx].isHeld = false;
    }

    public countHeldReels(): number {
        let count = 0;
        this.reels.forEach((reel) => {
            if (reel.isHeld) {
                count++;
            }
        });

        return count;
    }

    public releaseAllReels() {
        this.reels.forEach((reel) => {
            reel.isHeld = false;
        });
    }

    public allowStop() {
        this.isMinSpinTimeComplete = true;
        if (this.stopSymbolsAvailable) {
            this.stopReels(this.stopDelay);
        }
    }

    // chainSpin(delay = 0){
    //     if (this.stopSymbolsAvailable) {

    //     }
    // }

    public stopImmediately(stopData: any[][]) {
        this.resultData = stopData;
        Tween.killDelayedCallsTo(this.sound.playMusic);
        this.sound.fadeStop(
            SoundVars.EFFECT_REEL_SPIN,
            App.config.reelRunFadeOutTime || 0
        );
        // this.sound.restoreMusic(); //Crashes game when during freespins, spinning is stopped 2 times in a row
        SpinModel.stopImmediately = true;
        for (let reelIdx = 0; reelIdx < this.spinners.length; reelIdx++) {
            const s = this.spinners[reelIdx];
            Tween.killDelayedCallsTo(s.spin);
            s.activateQuickStop();
        }

        if (this.stoppingStarted) {
            for (
                let delayedCallIdx = 0;
                delayedCallIdx < this.stopDelayedCalls.length;
                delayedCallIdx++
            ) {
                const delayedCall = this.stopDelayedCalls[delayedCallIdx];
                if (delayedCall != null) {
                    delayedCall.progress = 1;
                }
            }
        } else {
            this.stopReels();
        }
    }

    public stop(
        stopData: any[][],
        delay = 0,
        spinificationData?: ISpinificationData
    ) {
        this.stopSymbolsAvailable = true;
        this.resultData = stopData;
        this.spinificationData = spinificationData;
        this.stopDelay = delay;
        if (this.isMinSpinTimeComplete) {
            this.stopReels(delay);
        }
    }

    /**
     * Stops the reels by using default stop data (the last stop positions from the SpinModel).
     */
    public stopDefault() {
        let reels = Array.from(Array(ReelGroup.instance.reels.length).keys());
        let stopData = SpinModel.getStopSymbolsForReels(reels);
        ReelGroup.instance.allowStop();
        ReelGroup.instance.stop(stopData);
    }

    public getStartSpinTime(): number {
        return this.spinTime;
    }

    protected stopReels(delay = 0) {
        if (!this.stoppingStarted) {
            if (!this.spinTime) {
                // Probably some generic error occured - reelgroup can not stop,
                // because it has not been started yet.
                return;
            }

            this.stoppingStarted = true;
            this.spinDelayedCalls.forEach((delayedCall) => delayedCall.kill());
            this.reelStopping = 0;
            // let delay = 0;
            let spinificationData =
                this.spinificationData || SpinModel.spinification;

            let delaySpinRatio: number[];
            let remainderSpinTime: number;
            if (this.config.individualReelSpinDelayConf) {
                delaySpinRatio = this.getDelaySpinRatio();
                remainderSpinTime =
                    (this.config.minSpinTime / 100) *
                    this.config.individualReelSpinDelayConf
                        .remainderStopReelsDelay;
            }

            for (let reelIdx = 0; reelIdx < this.reels.length; reelIdx++) {
                const r = this.reels[reelIdx];
                const s = this.spinners[reelIdx];

                if (!r.isHeld) {
                    // In case stop starts before the delayed spin
                    s.spin();
                }

                r.once(
                    GameEvents.REEL_STOPPED,
                    () => this.onReelStopped(reelIdx, spinificationData),
                    this
                );
                if (SpinModel.stopImmediately) {
                    delay += App.config.instantStopDelayTime;
                } else if (GameModel.turboSpin) {
                    delay += App.config.quickStopDelayTime;
                } else if (
                    (!BuyFeatureModel.isInBuyFeatureFreeSpins ||
                        GameModel.gameConfig.buyFSAnticipationDisable ==
                        undefined ||
                        GameModel.gameConfig.buyFSAnticipationDisable ==
                        false) &&
                    spinificationData &&
                    spinificationData.spinificationIndex <= reelIdx &&
                    spinificationData.spinificationEndIndex > 0 &&
                    spinificationData.spinificationIndex <=
                    spinificationData.spinificationEndIndex &&
                    (spinificationData.spinificationEndIndex >= reelIdx ||
                        this.hasAnticipation)
                ) {
                    this.hasAnticipation = true;
                    // let spinificationStartIndex = spinificationData.spinificationIndices? spinificationData.spinificationIndices[reelIdx] : reelIdx;
                    if (
                        reelIdx === 0 &&
                        spinificationData.spinificationEndIndex > reelIdx
                    ) {
                        // s.activateAnticipation();
                        // this.anticipation.play(reelIdx);
                        this.startAnticipation(reelIdx, spinificationData);
                    }
                    // s.activateAnticipation();
                    // delay += (spinificationStartIndex + 1) * App.config.reelSpinificationDuration;
                } else if (!r.isHeld) {
                    if (delaySpinRatio?.length) {
                        delay +=
                            (remainderSpinTime / 100) * delaySpinRatio[reelIdx];
                        delay -=
                            (this.config.stopTime / 100) *
                            delaySpinRatio[reelIdx];
                    } else {
                        delay += this.config.reelStopDelayTime;
                    }
                }

                if (!this.hasAnticipation) {
                    this.stopDelayedCalls.push(
                        this.addDelayedCall(
                            delay,
                            s.stop,
                            [this.resultData[reelIdx]],
                            s
                        )
                    );
                }

                // this.stopDelayedCalls.push(this.addDelayedCall(delay , s.stop, [SpinModel.getStopSymsForReel(reelIdx)], s));
            }
        }
    }

    protected getDelaySpinRatio(): number[] {
        let delaySpinRatio: number[] = [];
        if (
            typeof this.config.individualReelSpinDelayConf.delaySpinRatio[0] ===
            "number"
        ) {
            // we have only one array and return it
            delaySpinRatio =
                this.config.individualReelSpinDelayConf.delaySpinRatio;
        } else {
            // we have a two-dimensional array and we return the one that matches the number of rils
            for (
                let i = 0;
                i <
                this.config.individualReelSpinDelayConf.delaySpinRatio.length;
                i++
            ) {
                if (
                    this.config.individualReelSpinDelayConf.delaySpinRatio[i]
                        .length == this.reels.length
                ) {
                    delaySpinRatio =
                        this.config.individualReelSpinDelayConf.delaySpinRatio[
                        i
                        ];
                    break;
                }
            }
        }
        return delaySpinRatio;
    }

    public onReelStopped(
        reelIdx: number,
        spinificationData: ISpinificationData
    ) {
        this.reelsSpinning--;
        let nextreelIdx = reelIdx + 1;
        this.reelStopping = nextreelIdx;

        if (
            this.hasAnticipation &&
            spinificationData &&
            spinificationData.spinificationIndex <= nextreelIdx &&
            spinificationData.spinificationEndIndex > 0 &&
            spinificationData.spinificationIndex <=
            spinificationData.spinificationEndIndex &&
            (spinificationData.spinificationEndIndex >= nextreelIdx ||
                this.anticipationStarted)
        ) {
            // const s = this.spinners[nextreelIdx];
            // s.activateAnticipation();
            // this.anticipation.play(nextreelIdx);
            this.startAnticipation(nextreelIdx, spinificationData);
            // this.reels[nextreelIdx].spinnify();
        }
        this.anticipation.stop(this.reelsSpinning, reelIdx);
        if (this.reelsSpinning == 0) {
            this.dispatcher.emit(GameEvents.ALL_REELS_STOPPED);
            this.emit(GameEvents.ALL_REELS_STOPPED);
            this.onAllReelsStopped();

            let stopTime = new Date().getTime();

            ConsoleUtil.yellow(
                "[ReelGroup] SPIN DURATION: " +
                ((stopTime - this.spinTime) / 1000).toFixed(2)
            );
        }
    }

    public startAnticipation(
        reelId: number,
        spinificationData: ISpinificationData = this.spinificationData
    ) {
        const s = this.spinners[reelId];
        if (s) {
            this.anticipationStarted = true;
            let isAnticipatingReel =
                spinificationData.spinificationIndex <= reelId &&
                spinificationData.spinificationEndIndex >= reelId;
            if (isAnticipatingReel) {
                s.activateAnticipation();
                this.anticipation.play(reelId);
            } else {
                this.anticipation.stop(0, reelId);
            }
            let delay = isAnticipatingReel
                ? App.config.reelSpinificationDuration
                : 0;
            let stopTime = isAnticipatingReel
                ? App.config.anticipationStopTime
                : App.config.afterAnticipationReelStopTime;
            this.stopDelayedCalls.push(
                this.addDelayedCall(
                    delay,
                    s.stop,
                    [this.resultData[reelId], isAnticipatingReel, stopTime],
                    s
                )
            );
        }
    }

    public displaySummaryResult(winlinesToShow: IWinData[]) {
        if (GameModel.gameConfig.waysGame) {
            //** WAYS WIN **/
            this.showWaysSummary(winlinesToShow);
        } else {
            //** WIN LINES WIN **/
            this.showSummaryWinLines(winlinesToShow);
        }
    }

    public showFinalSymbols(finalSyms: number[][]) {
        for (let reelIdx = 0; reelIdx < this.reels.length; reelIdx++) {
            // let result = SpinModel.getFinalSymsForReel(reelIdx);
            let result = finalSyms[reelIdx];
            // TODO handling for more above and below symbols
            for (
                let symId = this.groupConfig.reelExtraSymbols;
                symId <
                this.reels[reelIdx].symbols.length -
                this.groupConfig.reelExtraSymbols;
                symId++
            ) {
                this.reels[reelIdx].symbols[symId].setId(
                    result[symId - this.groupConfig.reelExtraSymbols]
                );
            }

            this.reels[reelIdx].spinner.updateFinalSymbols();
        }
    }

    public displaySpinResults(
        winlinesToShow: IWinData[],
        repeat?: number,
        showBaners = false
    ) {
        if (!winlinesToShow || !winlinesToShow.length) {
            return;
        }
        if (GameModel.gameConfig.waysGame) {
            this.showWaysGroups(winlinesToShow, showBaners);
        } else {
            this.showWinLines(winlinesToShow, repeat, showBaners);
        }
    }

    public showWaysGroups(winsData, showBanner = false) {
        this.winGroupToAnim = 0;
        this.on(GameEvents.GROUP_ANIM_COMPLETE, () => {
            this.winGroupToAnim += 1;
            if (this.winGroupToAnim > winsData.length - 1) {
                this.winGroupToAnim = 0;
            }
            this.animateWaysGroup(winsData[this.winGroupToAnim].coordinates);
            this.winSymbolsView.getChildren().sort(this.symCompare.bind(this));
            if (showBanner) {
                this.winlinesView.setWinBannerValue(
                    winsData[this.winGroupToAnim].creditsWon
                );
            }
        });
        this.animateWaysGroup(winsData[this.winGroupToAnim].coordinates);
        if (showBanner) {
            this.winlinesView.setWinBannerValue(
                winsData[this.winGroupToAnim].creditsWon
            );
        }
        this.winSymbolsView.getChildren().sort(this.symCompare.bind(this));
    }

    public getWinAnimationName(symbol?: ReelSymbol): string {
        if (symbol?.isSpine) {
            return "win";
        } else {
            return "anim";
        }
    }

    protected showWaysOverlayWin(
        overlayType: OverlayType,
        group: { x: number; y: number }[],
        reelDelay: number
    ) {
        let overlay = SpinModel.getOverlay(overlayType);
        if (overlay) {
            overlay.initPositions.forEach((initPosition) => {
                let hasOverlayWin = false;
                group.forEach((item) => {
                    if (!hasOverlayWin) {
                        // Group is 1 based
                        // overlay data is 0 based
                        if (
                            item.x - 1 == initPosition.reel &&
                            item.y - 1 == initPosition.row
                        ) {
                            hasOverlayWin = true;
                        }
                    }
                });

                if (hasOverlayWin) {
                    const sym = this.getSymbolAt(
                        initPosition.reel,
                        initPosition.row
                    );
                    const anim = this.winSymbolsView.getAnimAtReelCell(
                        initPosition.reel,
                        initPosition.row
                    );
                    Tween.delayedCall(reelDelay * initPosition.reel, () => {
                        anim.showAnim(sym.id, this.getWinAnimationName(sym));
                    });
                }
            });
        }
    }

    /**
     * Animates a single ways group, by using color trails and alpha fade effects.
     *
     * @param {{ x: number; y: number }[]} group The group to animate, containing the symbol coordinates.
     * @memberof MDRReelGroup
     */
    public animateWaysGroup(group: { x: number; y: number }[]) {
        const reelDelay = this.areTrailsEnabled()
            ? Utils.isNumeric(App.config.trailsReelDelay)
                ? Number(App.config.trailsReelDelay)
                : 0.2
            : 0;
        this.showWaysOverlayWin(OverlayType.Expanding, group, reelDelay);

        if (!this.areTrailsEnabled()) {
            // let symbol;
            let counter = group.length;
            for (let i = 0; i < group.length; i++) {
                let symbol = this.getSymbolAt(group[i].x - 1, group[i].y - 1);
                symbol.showWin();
                this.winSymbolsView
                    .playAnimaAtReelCell(
                        group[i].x - 1,
                        group[i].y - 1,
                        symbol.getId(),
                        this.getWinAnimationName(symbol)
                    )
                    .then(() => {
                        counter--;
                        if (counter <= 0) {
                            this.emit(GameEvents.GROUP_ANIM_COMPLETE);
                        }
                    });

                // winAnim.showWin(symbol.getId(), false).then(() => {
                //     counter--;
                //     if (counter <= 0) {
                //         console.log("symbol anim complete");
                //         this.emit(GameEvents.GROUP_ANIM_COMPLETE);
                //     }
                // })
            }
        } else {
            // Trails logic
            if (
                SpinModel.isInFreespins() &&
                !this.animateTrailsInFreeSpinsMode()
            ) {
                return;
            }

            let winSymbols = new Array<number>();

            for (let index = 0; index < SpinModel.winsData.length; index++) {
                const data = SpinModel.winsData[index];
                for (
                    let winSymbolIndex = 0;
                    winSymbolIndex < data.winSymbols.length;
                    winSymbolIndex++
                ) {
                    const winSym: number = data.winSymbols[winSymbolIndex];
                    winSymbols.push(winSym);
                }
            }

            let firstSymbolId = this.getSymbolAt(
                group[0].x - 1,
                group[0].y - 1
            ).id;
            let color = this.getTrailColorForSymbol(firstSymbolId);

            if (stateMachine.currentState instanceof ShowWinState) {
                // We are still in summary state.
                this.fadeSymbols(1.0, 0.4, 1.0);
            } else {
                // We are in group animation state.
                if (winSymbols.length > group.length) {
                    // We have multiple groups to animate.
                    this.fadeSymbols(0.6, 0.25, 0.5);
                } else {
                    // We have a single group to animate.
                    this.fadeSymbols(1.0, 0.4, 0.5);
                }
            }

            let counter = group.length;
            let allPositions = new Array<Array<Point>>();

            for (let i = 0; i < group.length; i++) {
                // Coords starts from 1, that's why for indices we subtract one.
                let reelIdx = group[i].x - 1;
                let rowIdx = group[i].y - 1;

                // Checking the current symbol.
                let symbol = this.getSymbolAt(reelIdx, rowIdx);
                if (symbol.id == ReelGroup.EMPTY_SYM) {
                    // Hiding the empty symbols.
                    symbol.hideWin();
                    symbol.hide();
                    this.winSymbolsView
                        .getAnimAtReelCell(reelIdx, rowIdx)
                        .hideSymAnim();
                    this.winSymbolsView
                        .getAnimAtReelCell(reelIdx, rowIdx)
                        .hideWin();
                }

                // Getting the symbol animation
                let anim = this.winSymbolsView.getAnimAtReelCell(
                    reelIdx,
                    rowIdx
                );

                // And trying to find the symbol itself.
                let activeItem = group.find(
                    (groupItem) =>
                        groupItem.x == reelIdx + 1 && groupItem.y == rowIdx + 1
                );

                if (activeItem) {
                    if (!symbol.isSpine) {
                        // We found it, so let's animate its alpha
                        if (symbol.id == ReelGroup.EMPTY_SYM) {
                            // This is an empty symbol. We should hide it.
                            this.tweenInstances.push(
                                Tween.to(symbol, 0.1, { alpha: 0 })
                            );
                            this.tweenInstances.push(
                                Tween.to(anim, 0.1, { alpha: 0 })
                            );
                        } else {
                            // This is a winning symbol. Fading it in.
                            this.tweenInstances.push(
                                Tween.to(symbol, 0.5, { alpha: 1.0 })
                            );
                            this.tweenInstances.push(
                                Tween.to(anim, 0.5, { alpha: 1.0 })
                            );
                        }
                    }

                    // Crating trail(s) for the symbol.
                    let groupPositions = this.createTrail(
                        group,
                        group.indexOf(activeItem),
                        color
                    );
                    allPositions.push(groupPositions);

                    // And animating the symbol.
                    if (symbol.id != ReelGroup.EMPTY_SYM) {
                        let tween = Tween.delayedCall(
                            reelDelay * reelIdx,
                            () => {
                                this.winSymbolsView
                                    .playAnimaAtReelCell(
                                        activeItem.x - 1,
                                        activeItem.y - 1,
                                        symbol.getId(),
                                        this.getWinAnimationName(symbol)
                                    )
                                    .then(() => {
                                        counter--;
                                        if (counter <= 0) {
                                            this.emit(
                                                GameEvents.GROUP_ANIM_COMPLETE
                                            );
                                            // this.trailPool.stopAll();
                                        }
                                    });
                            }
                        );

                        this.tweenInstances.push(tween);
                    } else {
                        counter--;
                    }
                }
            }

            // Collecting a distinct collection of points
            let allPts = new Array<Point>();
            for (let i = 0; i < allPositions.length; i++) {
                const pts = allPositions[i];
                for (let j = 0; j < pts.length; j++) {
                    const pt = pts[j];
                    if (allPts.find((p) => p.x == pt.x && p.y == pt.y)) {
                        continue;
                    }

                    allPts.push(pt);
                }
            }

            let paths = WayAnalyzer.computeWays(allPts);
            let quality = 1.0;
            if (paths.length >= 30) {
                quality = 0.2;
            } else if (paths.length >= 25) {
                quality = 0.28;
            } else if (paths.length >= 20) {
                quality = 0.35;
            } else if (paths.length >= 15) {
                quality = 0.45;
            } else if (paths.length >= 10) {
                quality = 0.66;
            } else if (paths.length >= 5) {
                quality = 0.8;
            } else if (paths.length >= 3) {
                quality = 0.9;
            }

            // console.log("paths: " + paths.length + " | quality: " + quality);
            for (let i = 0; i < paths.length; i++) {
                const path = paths[i];
                Tween.delayedCall(
                    0.2 + 0.007 * i,
                    this.runMultiTrail,
                    [path, color, quality],
                    this
                );
            }
        }
    }

    public showWaysSummary(windata: IWinData[]) {
        if (!this.areTrailsEnabled()) {
            // TODO use the windata insteand of winningSymbols
            const winningSymbols = SpinModel.getWinningSymbols();
            this.symAnimCount = 0;

            for (let i = 0; i < winningSymbols.length; i++) {
                for (let j = 0; j < winningSymbols[i].length; j++) {
                    if (winningSymbols[i][j]) {
                        const symbol = this.getSymbolAt(i, j);
                        symbol.showWin();
                        this.symAnimCount++;

                        this.winSymbolsView
                            .playAnimaAtReelCell(
                                i,
                                j,
                                symbol.getId(),
                                this.getWinAnimationName(symbol)
                            )
                            .then(() => {
                                this.symAnimCount--;
                                if (this.symAnimCount == 0) {
                                    this.emit(
                                        GameEvents.WIN_ANIMATIONS_COMPLETE
                                    );
                                    ConsoleUtil.log(
                                        GameEvents.WIN_ANIMATIONS_COMPLETE
                                    );
                                }
                            });
                    }
                }
            }

            this.winSymbolsView.getChildren().sort(this.symCompare.bind(this));
        } else {
            this.showTrailsSummary(windata);
            // Commented out, because plays the anims twice
            // Trails logic
            if (SpinModel.isInFreespins()) {
                if (this.animateTrailsInFreeSpinsMode()) {
                    this.showTrailsSummary(windata);
                }
            } else {
                this.showTrailsSummary(windata);
            }

            // TODO use the windata insteand of winningSymbols
            const winningSymbols = SpinModel.getWinningSymbols();
            this.symACount = 0;

            for (let i = 0; i < winningSymbols.length; i++) {
                for (let j = 0; j < winningSymbols[i].length; j++) {
                    if (winningSymbols[i][j]) {
                        let symbol = this.getSymbolAt(i, j);

                        this.symACount++;

                        let rowIdx = j;

                        if (symbol.id == ReelGroup.EMPTY_SYM) {
                            symbol.hideWin();
                            symbol.hide();
                            this.winSymbolsView
                                .getAnimAtReelCell(i, j)
                                .hideSymAnim();
                            this.winSymbolsView
                                .getAnimAtReelCell(i, j)
                                .hideWin();

                            let offset = 1;

                            while (
                                this.getSymbolAt(i, j - offset).id ==
                                ReelGroup.EMPTY_SYM
                            ) {
                                offset++;
                            }

                            symbol = this.getSymbolAt(i, j - offset);

                            rowIdx = j - offset;

                            this.symACount--;
                        }

                        symbol.showWin();
                        this.winSymbolsView
                            .playAnimaAtReelCell(
                                i,
                                rowIdx,
                                symbol.getId(),
                                this.getWinAnimationName(symbol)
                            )
                            .then(() => {
                                this.symACount--;
                                if (this.symACount == 0) {
                                    this.emit(
                                        GameEvents.WIN_ANIMATIONS_COMPLETE
                                    );
                                    this.trailPool.destroyAll();
                                    ConsoleUtil.log(
                                        GameEvents.WIN_ANIMATIONS_COMPLETE
                                    );
                                }
                            });
                    }
                }
            }
        }

        this.winSymbolsView.getChildren().sort(this.symCompare.bind(this));
    }

    public showSummaryWinLines(
        winlinesToShow: IWinData[],
        playWinSound: boolean = true
    ) {
        if (winlinesToShow.length === 0) {
            this.finishShowingWinlines();
            return;
        }

        if (playWinSound) {
            this.winSound = SoundController.playSound(
                SoundVars[SpinModel.getWinsoundName()]
            );
        }

        this.off(GameEvents.GROUP_ANIM_COMPLETE);

        let repeatCount = GameModel.gameConfig.symAnimSummaryRepeat || 1;

        this.on(GameEvents.GROUP_ANIM_COMPLETE, () => {
            if (--repeatCount <= 0) {
                winlinesToShow.forEach((winline) =>
                    this.hideWinlineWin(winline)
                );
                this.finishShowingWinlines();
            } else {
                this.playAnimsOnce(winlinesToShow);
            }
        });
        this.winSymbolsView.getChildren().sort(this.symCompare.bind(this));
        this.playAnimsOnce(winlinesToShow);
    }

    protected playAnimsOnce(winlinesToShow: IWinData[]) {
        winlinesToShow.forEach((winline) => {
            winline.coordinates.forEach((winlinecoord) => {
                this.skimSymbolsDim.push(winlinecoord);
            });
            this.highlightWinlineWin(winline, false);
            this.winlinesView.highlightWinLine(winline, false);
        });
        this.skimSymbolsDim = [];
    }

    public showWinLines(
        winlinesToShow: IWinData[],
        repeat = -1,
        showBaners = false
    ) {
        if (winlinesToShow.length === 0) {
            this.finishShowingWinlines();
            return;
        }

        this.winGroupToAnim = 0;
        let competed = false;
        this.off(GameEvents.GROUP_ANIM_COMPLETE);
        this.on(GameEvents.GROUP_ANIM_COMPLETE, () => {
            let lastId = this.winGroupToAnim;
            this.winGroupToAnim += 1;
            if (this.winGroupToAnim > winlinesToShow.length - 1) {
                this.winGroupToAnim = 0;
                this.emit(GameEvents.WIN_ANIMATIONS_COMPLETE);
                competed = true;
                if (repeat > 0) {
                    repeat--;
                }

                if (repeat === 0) {
                    this.off(GameEvents.GROUP_ANIM_COMPLETE);
                }
            }
            if (!competed || repeat !== 0) {
                this.hideWinlineWin(winlinesToShow[lastId]);
                const winline = winlinesToShow[this.winGroupToAnim];
                this.highlightWinlineWin(winline, showBaners);
                this.winlinesView.highlightWinLine(winline);
                this.winSymbolsView
                    .getChildren()
                    .sort(this.symCompare.bind(this));
            }
        });
        const winlineData = winlinesToShow[this.winGroupToAnim];
        this.highlightWinlineWin(winlineData, showBaners);
        this.winlinesView.highlightWinLine(winlineData);
        this.winSymbolsView.getChildren().sort(this.symCompare.bind(this));
    }

    public showTotalWinnings() {
        // TODOCHANGE THIS Add INTERFACE
        // if (this.gameFactory instanceof CustomGameFactory) {
        //     const totalWinningsPopup = this.gameFactory.getClass("TotalWinningsPopup");
        //     totalWinningsPopup.create();
        //     this.parent.addChild(totalWinningsPopup);
        // }
    }

    /**
     * Highlights winning symbols on the reels and shows win values for a specific winline.
     * 
     * @param winlineData - Object containing data about the winning line
     * @param showWinValue - Boolean flag to determine if win value should be displayed
     */
    public highlightWinlineWin(winlineData: IWinData, showWinValue: boolean) {
        // Get the winning symbols for this winline
        const winSyms = this.getWinlineWinSymbols(winlineData);
        let ids = [];

        // Determine the target column index for showing win value
        // Most games pay for more than 2 symbols, but some rare cases pay for 2
        let targetColumnIndex = Math.min(2, winSyms.length - 1);

        // Dim all symbols except the winning ones
        this.reels.forEach((reel, reelIdx) => {
            reel.symbols.forEach((sym, rowIdx) => {
                let shouldDim = false;

                // Check if this symbol should be dimmed based on skimSymbolsDim
                if (!showWinValue) {
                    this.skimSymbolsDim.forEach((coord) => {
                        if (coord.x - 1 === reelIdx && coord.y === rowIdx - 1) {
                            shouldDim = true;
                        }
                    });
                }

                // Apply dimming effect
                if (sym?.spineAnim?.state.hasAnimation("dimmed") && !shouldDim) {
                    sym.playAnimation("dimmed");
                } else if (!shouldDim) {
                    sym.alpha = 0.8;
                }
            });
        });

        // Highlight winning symbols and show win value
        for (let symIdx = 0; symIdx < winSyms.length; symIdx++) {
            const symbol: ReelSymbol = winSyms[symIdx];

            // Show win value on the target column if showWinValue is true
            if (symbol.getReelIdx() == targetColumnIndex && showWinValue) {
                let index = 1 + Math.floor(symbol.y / Layout.scalePixels(this.symBounds.height));
                this.winlinesView.showLineValue(index, winlineData.creditsWon);
            }

            // Highlight the winning symbol
            symbol.showWin(true);
            ids.push(symbol.getId());
        }

        // Play win animations for the winning symbols
        this.winSymbolsView.playWinLineSymbols(winlineData, ids).then(() => {
            this.emit(GameEvents.GROUP_ANIM_COMPLETE);
        });
    }

    public hideWinlineWin(winlineData: IWinData) {
        let winlineWinSymbols = this.getWinlineWinSymbols(winlineData);
        winlineWinSymbols.forEach((symbol) => {
            if (!SpinModel.bonusWin) {
                symbol.hideWin();
            } else {
                if (
                    symbol.reelIdx >= SpinModel.firstWildIndex &&
                    symbol.reelIdx <
                    SpinModel.firstWildIndex + SpinModel.wildVersion - 1
                ) {
                    if (
                        this.reels[symbol.reelIdx].symbols.indexOf(symbol) == 0
                    ) {
                        symbol.hideWin();
                    }
                } else {
                    symbol.hideWin();
                }
            }
        });
        this.winSymbolsView.hideWinlineWinSymbols(winlineData);
    }

    public finishShowingWinlines() {
        // this.dispatcher.emit(GameEvents.SHOW_WIN_LINES_COMPLETE);
        this.emit(GameEvents.WIN_ANIMATIONS_COMPLETE);
    }

    public getWinlineWinSymbols(winLineData: IWinData): ReelSymbol[] {
        const winningReels = winLineData.winReels;
        const winningPositions = winLineData.winSymbols;

        const winSymbols: ReelSymbol[] = [];

        for (
            let winReelIdx = 0;
            winReelIdx < winningReels.length;
            winReelIdx++
        ) {
            const reelIdx = winningReels[winReelIdx];
            const symIdx =
                winningPositions[winReelIdx] +
                this.groupConfig.reelExtraSymbols; // Win positions start from 1

            const reel = this.reels[reelIdx];
            const sym = reel.symbols[symIdx];

            winSymbols.push(sym);
        }

        return winSymbols;
    }

    public clearWinInfoLabel() {
        // this.winlinesView.winValueBanner.visible = false;
        if (this.winInfoLabelBg) {
            Tween.to(this.winInfoLabelBg, 0.1, {
                alpha: 0,
                onComplete: () => {
                    this.parent.removeChild(this.winInfoLabelBg);
                    this.winInfoLabelBg = null;
                },
                callbackScope: this,
            });
        }

        if (this.winInfoLabelTF) {
            Tween.to(this.winInfoLabelTF, 0.1, {
                alpha: 0,
                onComplete: () => {
                    this.parent.removeChild(this.winInfoLabelTF);
                    this.winInfoLabelTF = null;
                },
                callbackScope: this,
            });
        }
    }

    /**
     *
     * @param data {IWinData}
     */
    public createWinInfoLabel(data: IWinData) {
        let scale = false;
        if (!this.winInfoLabelBg) {
            this.winInfoLabelBg = this.assets.getSprite("winInfoLabelBg");
            this.winInfoLabelBg.anchor.set(0.5, 0.5);
            this.winInfoLabelBg.width = Layout.relWidth(
                this.config.winInfoLabelRect.width
            );
            this.winInfoLabelBg.height = Layout.relHeight(
                this.config.winInfoLabelRect.height
            );
            Layout.horizontalAlignCenter(
                this.winInfoLabelBg,
                this.config.winInfoLabelRect.x
            );
            Layout.verticalAlignMiddle(
                this.winInfoLabelBg,
                this.config.winInfoLabelRect.y
            );
            this.parent.addChild(this.winInfoLabelBg);
            scale = true;
        }

        let text;
        if (data.lineId >= 0) {
            text =
                this.locale.getString("flash_dynamibox_line") +
                " " +
                (data.lineId + 1) +
                "\n";
            text =
                this.locale.getString("gui_dynamic_line") +
                " " +
                (data.lineId + 1) +
                "\n";
        } else if (data.winSymId === this.config.bonusSymId) {
            text = this.locale.getString("flash_dynamicbox_line_bous") + "\n";
            text = this.locale.getString("gui_dynamic_bonus") + "\n";
        } else {
            text = this.locale.getString("flash_dynamicbox_line_scater") + "\n";
            text = this.locale.getString("gui_dynamic_scatter") + "\n";
        }

        if (data.creditsWon > 0) {
            text += CurrencyUtils.formatCurrency(
                data.creditsWon,
                BetModel.currencyLocalization
            );
        } else {
            text += this.locale.getString("gui_lineselector_win");
        }

        if (!this.winInfoLabelTF) {
            this.winInfoLabelTF = this.assets.getTF(
                text,
                this.winInfoLabelBg.height * 0.35
            );
            this.winInfoLabelTF.anchor.set(0.5, 0.5);
            this.winInfoLabelTF.x = this.winInfoLabelBg.x;
            this.winInfoLabelTF.y = this.winInfoLabelBg.y;
            this.parent.addChild(this.winInfoLabelTF);
            scale = true;
        } else {
            this.winInfoLabelTF.text = text;
        }

        if (scale) {
            Tween.from(this.winInfoLabelTF.scale, 0.2, { x: 0, y: 0 });
            Tween.from(this.winInfoLabelBg.scale, 0.2, { x: 0, y: 0 }); // ??????
        }
    }

    public getSymbolAt(reelId: number, rowId: number): ReelSymbol {
        return this.reels[reelId].symbols[
            rowId + this.groupConfig.reelExtraSymbols
        ];
    }

    public setSymbolAt(reelId: number, rowId: number, symId: number) {
        this.reels[reelId].symbols[
            rowId + this.groupConfig.reelExtraSymbols
        ].setId(symId);
    }

    public revealAllSymbols() {
        this.reels.forEach((reel) => {
            reel.symbols.forEach((symbol) => {
                symbol.forceHidden = false;
                symbol.show();
            });
        });
    }

    public showSymbolAt(position: {
        reelId: number;
        row: number;
        id?: number;
    }) {
        let sym = this.getSymbolAt(position.reelId, position.row);
        if (sym) {
            sym.show();
        } else {
            ConsoleUtil.red(
                "Trying to show missing symbol at:" +
                position.reelId +
                "x" +
                position.row
            );
        }
        if (!isNaN(position.id)) {
            sym.setId(position.id);
        }
    }

    public hideSymbolAt(position: { reelId: number; row: number }) {
        let sym = this.getSymbolAt(position.reelId, position.row);
        if (sym) {
            if (this.spinners[position.reelId].isSettled()) {
                sym.hide();
            } else {
                this.reels[position.reelId].once(
                    GameEvents.REEL_STOPPED,
                    () => {
                        sym = this.getSymbolAt(position.reelId, position.row);
                        sym.hide();
                    }
                );
            }
        } else {
            ConsoleUtil.red(
                "Trying to hide missing symbol at:" +
                position.reelId +
                "x" +
                position.row
            );
        }
    }

    // SWIPE FUNCTIONALYTY
    public enableSwipeSpin() {
        let swiper = this.scene.shapes.get("reels_hit");
        if (swiper) {
            this.addSwipeListeners();
        }
    }

    public disableSwipeSpin() {
        let swiper = this.scene.shapes.get("reels_hit");
        if (swiper) {
            this.removeSwipeListeners();
        }
    }

    public addSwipeListeners() {
        let swiper = this.scene.shapes.get("reels_hit");
        swiper.interactive = true;
        swiper.once(PointerEvents.DOWN, this.onSwipeStart, this);

        if (!this._onSwipeCancel) {
            this._onSwipeCancel = this.onSwipeCancel.bind(this);
        }
    }

    public removeSwipeListeners() {
        let swiper = this.scene.shapes.get("reels_hit");
        swiper.interactive = false;
        swiper.off(PointerEvents.DOWN, this.onSwipeStart, this);
        swiper.off(PointerEvents.UP, this.onSwipeEnd, this);
        swiper.off(PointerEvents.UPOUTSIDE, this.onSwipeEnd, this);
        swiper.off(PointerEvents.MOVE, this.onSwipeMove, this);
        // document.removeEventListener(PointerEvents.ROLLOUT,this._onSwipeCancel);
        App.instance.stage.off(PointerEvents.ROLLOUT, this._onSwipeCancel);
        document.removeEventListener("visibilitychange", this._onSwipeCancel);
        App.dispatcher.off(GameEvents.RESIZE, this.onResize, this);
    }

    protected onResize(orientationChanged = false) {
        if (orientationChanged) {
            this.onSwipeCancel(null);
        }
    }

    protected onSwipeStart(e: InteractionEvent) {
        let swiper = this.scene.shapes.get("reels_hit");
        this.hideLinesAndInfo(true);
        swiper.once(PointerEvents.UP, this.onSwipeEnd, this);
        swiper.once(PointerEvents.UPOUTSIDE, this.onSwipeEnd, this);
        swiper.once(PointerEvents.CANCEL, this.onSwipeCancel, this);
        App.instance.stage.once(PointerEvents.ROLLOUT, this._onSwipeCancel);
        App.dispatcher.once(GameEvents.RESIZE, this.onResize, this);
        // document.addEventListener(PointerEvents.ROLLOUT,this._onSwipeCancel);
        document.addEventListener("visibilitychange", this._onSwipeCancel);
        this.swipePos = this.getPos(e.data.originalEvent);
        this.spinners.forEach((spinner) => {
            spinner.setupSwipe(0.8);
        });
        swiper.on(PointerEvents.MOVE, this.onSwipeMove, this);
    }
    protected async onSwipeCancel(e) {
        let swiper = this.scene.shapes.get("reels_hit");
        swiper.off(PointerEvents.UP, this.onSwipeEnd, this);
        swiper.off(PointerEvents.UPOUTSIDE, this.onSwipeEnd, this);
        swiper.off(PointerEvents.CANCEL, this.onSwipeCancel, this);
        swiper.off(PointerEvents.MOVE, this.onSwipeMove, this);
        let promisses = [];
        this.spinners.forEach((spinner) => {
            promisses.push(spinner.resetSwipe(0.2));
        });

        await PromiseHelpers.combineParallel(promisses);
        swiper.once(PointerEvents.DOWN, this.onSwipeStart, this);
    }

    protected async onSwipeEnd(e) {
        let swiper = this.scene.shapes.get("reels_hit");
        swiper.off(PointerEvents.UP, this.onSwipeEnd, this);
        swiper.off(PointerEvents.UPOUTSIDE, this.onSwipeEnd, this);
        swiper.off(PointerEvents.CANCEL, this.onSwipeCancel, this);
        swiper.off(PointerEvents.MOVE, this.onSwipeMove, this);
        // document.removeEventListener(PointerEvents.ROLLOUT,this._onSwipeCancel);
        App.instance.stage.off(PointerEvents.ROLLOUT, this._onSwipeCancel);
        App.dispatcher.off(GameEvents.RESIZE, this.onResize, this);
        document.removeEventListener("visibilitychange", this._onSwipeCancel);
        if (this.spinners[0].canSwipeSpin(0.5)) {
            this.spinners.forEach((spinner) => {
                spinner.setSwipeSpin();
            });
            CommandManager.execute(Commands.SPIN);
        } else {
            let promisses = [];
            this.spinners.forEach((spinner) => {
                promisses.push(spinner.resetSwipe(0.5));
            });
            await PromiseHelpers.combineParallel(promisses);

            swiper.once(PointerEvents.DOWN, this.onSwipeStart, this);
        }
    }

    protected onSwipeMove(e: InteractionEvent) {
        let dy = this.getPos(e.data.originalEvent) - this.swipePos;
        this.swipePos = this.getPos(e.data.originalEvent);
        if (
            !GameModel.gameConfig.debugLogging ||
            GameModel.gameConfig.debugLogging.pointerEvents === true
        ) {
            ConsoleUtil.log(e.data.originalEvent, dy, this.swipePos);
        }
        this.spinners.forEach((spinner) => {
            if (spinner.canSwipe(dy * spinner.getDirection())) {
                spinner.updateSwipeSpeed(dy * spinner.getDirection());
                spinner.spinLoopUpdate(dy * spinner.getDirection());
            }
        });
    }

    //Override for horizontal reels
    protected getPos(originalEvent): number {
        if (originalEvent.touches) {
            return originalEvent.touches[0].pageY;
        } else if (originalEvent instanceof MouseEvent) {
            return originalEvent.pageY;
        }
    }

    /**
     * Gets the symbol trail color. Used for the ways group particle animation.
     *
     * Override at the derived class to define the trails colors
     * for each symbol id.
     *
     * @protected
     * @param {number} id The symbol id.
     * @return {*}  {string} The color of the trail for this symbol.
     * @memberof ReelGroup
     */
    protected getTrailColorForSymbol(id: number): Color {
        //return "#feffa3";
        let rgb = PixiUtils.hex2rgb(0xfeffa3);
        return { r: rgb[0] * 255, g: rgb[1] * 255, b: rgb[2] * 255 };
    }

    /**
     * Defines how the trails will be drawn, when animating the ways group.
     * Override at the derived class to change the default value [true].
     *
     * Example: Games like SherlockBones would return true.
     * Games like MightyDragons would return false.
     * It depends on the game logic.
     * @protected
     * @return {*}  {boolean} Return false at the derived class to connect cells,
     * which are not directly connected.
     * @memberof ReelGroup
     */
    protected createTrailsOnlyBetweenDirectlyConnectedCells(): boolean {
        return true;
    }

    /**
     * Defines whether the ways groups' trails will be active in free spins mode.
     * Override at the derived class to change the default value [false].
     * @protected
     * @return {*}  {boolean} Return true at the derived class to enable this feature.
     * Note that in free spins you might notice performance issues, because usually
     * the winning symbols count increases, compared to those in normal game mode.
     * @memberof ReelGroup
     */
    protected animateTrailsInFreeSpinsMode(): boolean {
        return false;
    }

    /**
     * Override at the derived class to disable trails completely.
     *
     * Trails are particles effect lines like commets, which travels
     * between winning group of symbols.
     *
     * @protected
     * @return {*}  {boolean} Return false at the derived class to disable ways groups trails.
     * @memberof ReelGroup
     */
    protected areTrailsEnabled(): boolean {
        return true;
    }

    destroy() {
        this.winlinesView.destroy();
        this.winSymbolsView.destroy();
        super.destroy();
    }

    /**
     * Fade outs the win/nowin symbols by providing alpha values and anim speed.
     *
     * Note: the winning symbols at the currently active ways group will be animated separately to alpha of 1.0.
     * @private
     * @param {number} [winSymAlpha] The target alpha for the win symbols.
     * @param {number} [nonWinSymAlpha] The target alpha for the other (non-win) symbols.
     * @param {number} [speed] The animation duration in seconds.
     * @memberof MDRReelGroup
     */
    protected fadeSymbols(
        winSymAlpha: number = 0.6,
        nonWinSymAlpha: number = 0.3,
        speed: number = 0.5
    ): void {
        let winningSymbols = SpinModel.getWinningSymbols();

        // Getting the reel final symbols
        let reels: number[] = Array.from(Array(this.reels.length).keys());
        let reelSymbols: number[][] = SpinModel.getFinalSymsForReels(reels);

        let overlay = SpinModel.getOverlay(OverlayType.Locked);
        for (let reelIdx = 0; reelIdx < this.reels.length; reelIdx++) {
            let maxSymId = reelSymbols[reelIdx].length;

            for (let symIdx = 0; symIdx < maxSymId; symIdx++) {
                let symbol = this.getSymbolAt(reelIdx, symIdx);
                if (symbol.isSpine) {
                    return;
                }

                let anim = this.winSymbolsView.getAnimAtReelCell(
                    reelIdx,
                    symIdx
                );

                if (winningSymbols[reelIdx]?.[symIdx]) {
                    // This is a win symbol.
                    // fade not locked winning symbols
                    if (overlay) {
                        overlay.symbolPosition.forEach((reel) => {
                            reel.forEach((symbId) => {
                                if (
                                    symbId != winningSymbols[reelIdx]?.[symIdx]
                                ) {
                                    this.tweenInstances.push(
                                        Tween.to(symbol, speed, {
                                            alpha: winSymAlpha,
                                        })
                                    );
                                    this.tweenInstances.push(
                                        Tween.to(anim, speed, {
                                            alpha: winSymAlpha,
                                        })
                                    );
                                }
                            });
                        });
                    } else {
                        //fades symbols
                        this.tweenInstances.push(
                            Tween.to(symbol, speed, { alpha: winSymAlpha })
                        );
                        this.tweenInstances.push(
                            Tween.to(anim, speed, { alpha: winSymAlpha })
                        );
                    }
                } else {
                    // This a non-win symbol.
                    this.tweenInstances.push(
                        Tween.to(symbol, speed, { alpha: nonWinSymAlpha })
                    );
                    this.tweenInstances.push(
                        Tween.to(anim, speed, { alpha: nonWinSymAlpha })
                    );
                }
            }
        }
    }

    /**
     * Creates a colorful trail animation for a given group symbol
     * and executes it after internally calculated delay.
     *
     * @private
     * @param {{ x: number; y: number }[]} group The group, where the symbol belongs.
     * @param {number} index The index of the target symbol.
     * @param {string} [color] The trail color.
     * @memberof ReelGroup
     */
    protected createTrail(
        group: { x: number; y: number }[],
        index: number,
        color: Color
    ): Point[] {
        const useMulti = true;

        let currentAnim = this.winSymbolsView.getAnimAtReelCell(
            group[index].x - 1,
            group[index].y - 1
        );

        let positions = new Array<Point>();

        for (let i = 0; i < group.length; i++) {
            // If it's the next reel
            if (group[i].x == group[index].x + 1) {
                if (
                    this.createTrailsOnlyBetweenDirectlyConnectedCells() &&
                    !(Math.abs(group[i].y - group[index].y) <= 1)
                ) {
                    continue;
                }

                let nextAnim = this.winSymbolsView.getAnimAtReelCell(
                    group[i].x - 1,
                    group[i].y - 1
                );

                positions.push(new Point(currentAnim.x, currentAnim.y));
                positions.push(new Point(nextAnim.x, nextAnim.y));
                if (useMulti) {
                } else {
                    Tween.delayedCall(
                        (group[i].x - 1) * 0.2,
                        this.runTrail,
                        [currentAnim, nextAnim, color],
                        this
                    );
                }
            }
        }

        return positions;
    }

    private findDuplicates<T>(
        arr: Array<T>,
        predicate: (el1: T, el2: T) => boolean
    ): Array<T> {
        let sorted_arr = arr.slice().sort();
        let results: T[] = [];
        for (let i = 0; i < sorted_arr.length - 1; i++) {
            if (predicate(sorted_arr[i + 1], sorted_arr[i])) {
                results.push(sorted_arr[i]);
            }
        }

        return results;
    }

    /**
     * Tween delayed method callback, which will be executed after pre-defined delay,
     * when the trail has been created. Usage:
     * ```
     * Tween.delayedCall(
     *              someDelay,
     *              this.runTrail,
     *              [argsToRunTrail..],
     *              this
     *           );
     * ```
     * Use it also, when you need to kill all related tweens:
     * ```
     * Tween.killDelayedCallsTo(this.runTrail);
     * ```
     * @private
     * @param {SymbolAnimation} start
     * @param {SymbolAnimation} end
     * @param {string} [startColor]
     * @param {string} [endColor]
     * @memberof ReelGroup
     */
    protected runTrail(
        start: Point | SymbolAnimation,
        end: Point | SymbolAnimation,
        color: Color
    ) {
        let useSprite = false;
        let positions = new Array<Point>(
            new Point(start.x, start.y),
            new Point(end.x, end.y)
        );

        if (useSprite) {
            let trail = new Trail(positions, () => {
                trail = null;
            });
            this.winSymbolsView.addChildAt(trail, 0);
        } else {
            let startColor = PixiUtils.hex2string(
                PixiUtils.rgb2hex([color.r / 255, color.g / 255, color.b / 255])
            );
            let trail = new ColorfulTrail(
                new Point(start.x, start.y),
                new Point(end.x, end.y),
                () => {
                    trail = null;
                },
                startColor
            );

            this.winSymbolsView.addChildAt(trail, 0);
        }
    }

    protected runMultiTrail(
        positions: Point[],
        color: Color,
        quality: number = 1.0
    ) {
        if (!this.winSymbolsView.children.includes(this.trailPool.container)) {
            if (!this.waysLinesAboveSymbols) {
                this.winSymbolsView.addChildAt(this.trailPool.container, 0);
            } else {
                this.winSymbolsView.addChild(this.trailPool.container);
            }
        }

        let trail = this.trailPool.getTrail(color, quality);
        trail.start(positions);
    }

    /**
     * Draws all the possible ways, using trails.
     *
     * @private
     * @param {IWinData[]} winData data, containing the win symbols coordinates.
     * @memberof ReelGroup
     */
    protected showTrailsSummary(winData: IWinData[]) {
        for (let index = 0; index < winData.length; index++) {
            const group = winData[index].coordinates;
            this.animateWaysGroup(group);
        }

        let delay = SpinModel.isInFreespins() ? 2.0 : 1.7;
        Tween.delayedCall(delay, () => {
            Tween.killDelayedCallsTo(this.runTrail);
            Tween.killDelayedCallsTo(this.runMultiTrail);
            this.emit(GameEvents.WIN_ANIMATIONS_COMPLETE);
            this.trailPool.destroyAll();
        });
    }

    /**
     * (Re)creates the pixi graphics object, used to show the reel masks on the screen.
     *
     * @protected
     * @memberof ReelGroup
     */
    protected createReelMaskDebugBoundsRect() {
        if (this.reelMaskDebugGraphics && this.reelMaskDebugGraphics.parent) {
            this.reelMaskDebugGraphics.parent.removeChild(
                this.reelMaskDebugGraphics
            );
            this.reelMaskDebugGraphics.destroy();
        }

        this.reelMaskDebugGraphics = new Graphics();
        this.reels.forEach((reel) => {
            this.reelMaskDebugGraphics.beginFill(
                reel.reelIndex % 2 == 0 ? 0x00ff : 0x00ff00,
                0.3
            );
            this.reelMaskDebugGraphics.drawRect(
                reel.mask.x,
                reel.mask.y,
                reel.mask.width,
                reel.mask.height
            );
            this.reelMaskDebugGraphics.endFill();
        });

        if (!App.instance.debugBounds) {
            this.reelMaskDebugGraphics.alpha = 0;
        }

        this.addChild(this.reelMaskDebugGraphics);
    }

    protected onAllReelsStopped(): void {
        // Will be called, when all the reels has been stopped.
        // Override at derived class if you need to.
    }
}

export default ReelGroup;
